import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { lastErrorRef, sendProblemReport } from '../lib/issue-reporter';

/**
 * "Report a problem" dialog. Shared by the display, upload and moderation
 * apps (identical copies, with report-problem.css). Uses a native <dialog>
 * so focus trapping, Escape and the backdrop come from the browser.
 *
 * Copy follows house style: plain words, no em dashes.
 */

const FALLBACK_EMAIL = 'archive@brisbanepride.org.au';

interface ReportProblemDialogProps {
  open: boolean;
  onClose: () => void;
  /** Reference of an error the person just saw, to link the two. */
  relatedRef?: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; ref: string }
  | { kind: 'failed' };

export function ReportProblemDialog({ open, onClose, relatedRef }: ReportProblemDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [showError, setShowError] = useState(false);
  const id = useId();
  const linkedRef = relatedRef ?? lastErrorRef();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      // The upload form is framed in a WordPress page and the frame grows to
      // its full content height, so a centred dialog can sit off screen. Pin
      // it to the top of the frame and ask the page to scroll there (the
      // same message the wizard uses between steps).
      if (window.parent !== window) {
        dialog.classList.add('report-problem--embedded');
        window.parent.postMessage({ type: 'pride-upload-scrollto-top' }, '*');
      }
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  // Start fresh each time the dialog opens after a sent report. A failed
  // send keeps the text so nothing the person wrote is lost.
  useEffect(() => {
    if (open && status.kind === 'sent') {
      setMessage('');
      setStatus({ kind: 'idle' });
    }
    if (open) setShowError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      setShowError(true);
      return;
    }
    setStatus({ kind: 'sending' });
    try {
      const ref = await sendProblemReport({
        message: message.trim(),
        email: email.trim(),
        relatedRef: linkedRef,
        website,
      });
      setStatus({ kind: 'sent', ref });
    } catch {
      setStatus({ kind: 'failed' });
    }
  };

  const mailto = `mailto:${FALLBACK_EMAIL}?subject=${encodeURIComponent(
    'Problem with Pride History',
  )}&body=${encodeURIComponent(
    `${message}\n\nPage: ${typeof location !== 'undefined' ? location.origin + location.pathname : ''}${
      linkedRef ? `\nError reference: ${linkedRef}` : ''
    }`,
  )}`;

  return (
    <dialog
      ref={dialogRef}
      className="report-problem"
      aria-labelledby={`${id}-title`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself.
        if (e.target === dialogRef.current) onClose();
      }}
    >
      {open && (
        <div className="report-problem__panel">
          <div className="report-problem__header">
            <h2 id={`${id}-title`} className="report-problem__title">
              {status.kind === 'sent' ? 'Thanks, we have it' : 'Report a problem'}
            </h2>
            <button
              type="button"
              className="report-problem__close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {status.kind === 'sent' ? (
            <div className="report-problem__done" role="status">
              <p>
                Your reference is <strong className="report-problem__ref">{status.ref}</strong>.
              </p>
              <p>
                We read every report.{' '}
                {email.trim()
                  ? 'We will reply to the email you gave us.'
                  : 'You did not leave an email, so we will not be able to reply, but we will look into it.'}
              </p>
              <div className="report-problem__actions">
                <button type="button" className="report-problem__primary" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form className="report-problem__form" onSubmit={handleSubmit} noValidate>
              <p className="report-problem__intro">
                Tell us what went wrong and we will look into it.
              </p>

              <label className="report-problem__label" htmlFor={`${id}-message`}>
                What happened?
              </label>
              <p className="report-problem__hint" id={`${id}-message-hint`}>
                What were you trying to do, and what did you see?
              </p>
              <textarea
                id={`${id}-message`}
                className="report-problem__textarea"
                rows={5}
                maxLength={4000}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (showError) setShowError(false);
                }}
                aria-describedby={`${id}-message-hint${showError ? ` ${id}-message-error` : ''}`}
                aria-invalid={showError || undefined}
                autoFocus
              />
              {showError && (
                <p className="report-problem__error" id={`${id}-message-error`}>
                  Please tell us a little about the problem.
                </p>
              )}

              <label className="report-problem__label" htmlFor={`${id}-email`}>
                Your email <span className="report-problem__optional">(optional)</span>
              </label>
              <p className="report-problem__hint" id={`${id}-email-hint`}>
                Only if you would like a reply.
              </p>
              <input
                id={`${id}-email`}
                className="report-problem__input"
                type="email"
                autoComplete="email"
                maxLength={200}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={`${id}-email-hint`}
              />

              {/* Honeypot. Hidden from people and screen readers. */}
              <div className="report-problem__hp" aria-hidden="true">
                <label htmlFor={`${id}-website`}>Website</label>
                <input
                  id={`${id}-website`}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <p className="report-problem__smallprint">
                We also send the page address, your browser and screen size
                {linkedRef ? `, and the error you just saw (${linkedRef})` : ', and any recent error'}.
                Never your files or anything you have typed into a form.
              </p>

              {status.kind === 'failed' && (
                <p className="report-problem__error" role="alert">
                  We could not send that just now. Try again, or{' '}
                  <a href={mailto}>email it to us instead</a>.
                </p>
              )}

              <div className="report-problem__actions">
                <button type="button" className="report-problem__secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="report-problem__primary"
                  disabled={status.kind === 'sending'}
                >
                  {status.kind === 'sending' ? 'Sending...' : 'Send report'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </dialog>
  );
}

/** Flag icon shown on every prominent "Report a problem" trigger. */
export function ReportProblemIcon() {
  return (
    <svg
      className="report-problem-button__icon"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3.5 14.5V2M3.5 2.75h8l-1.75 3 1.75 3h-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface ReportProblemTriggerProps {
  onClick: () => void;
  /** Extra classes for placement, added to report-problem-button. */
  className?: string;
  /** Shorter label shown on narrow screens, e.g. "Report". */
  shortLabel?: string;
  'data-testid'?: string;
}

/**
 * The prominent trigger: a pill button with a flag icon, for headers and
 * top bars. The accessible name is always "Report a problem", whichever
 * label is showing.
 */
export function ReportProblemTrigger({
  onClick,
  className,
  shortLabel,
  'data-testid': testId,
}: ReportProblemTriggerProps) {
  return (
    <button
      type="button"
      className={className ? `report-problem-button ${className}` : 'report-problem-button'}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-label="Report a problem"
      data-testid={testId}
    >
      <ReportProblemIcon />
      <span className={shortLabel ? 'report-problem-button__long' : undefined}>
        Report a problem
      </span>
      {shortLabel && <span className="report-problem-button__short">{shortLabel}</span>}
    </button>
  );
}

interface ReportProblemButtonProps {
  className?: string;
  label?: string;
  relatedRef?: string;
  /** 'link' is a text link for footers; 'button' is the prominent pill. */
  variant?: 'link' | 'button';
  shortLabel?: string;
}

/** A button that opens the dialog. Drop it anywhere. */
export function ReportProblemButton({
  className,
  label = 'Report a problem',
  relatedRef,
  variant = 'link',
  shortLabel,
}: ReportProblemButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === 'button' ? (
        <ReportProblemTrigger
          className={className}
          shortLabel={shortLabel}
          onClick={() => setOpen(true)}
        />
      ) : (
        <button
          type="button"
          className={className ?? 'report-problem-link'}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
        >
          {label}
        </button>
      )}
      <ReportProblemDialog open={open} onClose={() => setOpen(false)} relatedRef={relatedRef} />
    </>
  );
}
